import type { DayEntrySummary } from '@/hooks/monitor/useTodayEntriesByUser'

function formatClockTime(iso: string): string {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

// Structured result so the renderer can place `primary` and `qualifier` in
// separate inline elements with flex-wrap, letting long copy like
// "(at Spartan Fire Reno since 11:30 AM)" break to its own line on narrow
// cards instead of getting ellipsized.
export interface StartedLine {
    primary: string
    qualifier?: string
}

// Rules:
//   1. Single time:         primary="Started 7:45 AM · Elm St Reno"
//                           qualifier=undefined
//      — shift start equals current active entry; show job inline.
//   2. Two times with job:  primary="Started 7:45 AM"
//                           qualifier="(at Elm St Reno since 11:30 AM)"
//   3. Two times no job:    primary="Started 7:45 AM"
//                           qualifier="(switched at 11:30 AM)"
//   4. Clocked out today:   primary="Started 7:45 AM", qualifier=undefined
//   5. No shift today:      null (line hidden entirely)
export function formatStartedLine(dayEntry: DayEntrySummary | undefined): StartedLine | null {
    if (!dayEntry) return null
    const shiftTime = formatClockTime(dayEntry.shiftStartAt)
    const current = dayEntry.currentEntry

    if (!current) {
        return { primary: `Started ${shiftTime}` }
    }

    const jobLabel = current.jobName ?? current.locationName
    const sameTime =
        Math.abs(new Date(current.clockIn).getTime() - new Date(dayEntry.shiftStartAt).getTime()) <
        1000

    if (sameTime) {
        return {
            primary: jobLabel ? `Started ${shiftTime} · ${jobLabel}` : `Started ${shiftTime}`,
        }
    }

    const currentTime = formatClockTime(current.clockIn)
    return {
        primary: `Started ${shiftTime}`,
        qualifier: jobLabel
            ? `(at ${jobLabel} since ${currentTime})`
            : `(switched at ${currentTime})`,
    }
}
